import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../Auth/AuthProvider';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import { useSession } from '../../Context/ReadingSessionProvider';
import {
  ArticleActivityEvent,
  ArticleActivityStage,
} from '../../Session/SessionEvents/ArticleActivityEvent';
import { Article } from '../../Types/Article';

interface UseArticleActivityResult {
  // Metrics
  activeTime: number;
  wordsRead: number;
  isArticleRead: boolean;

  // Actions
  recordWordsRead: (wordCount: number) => void;
  markArticleAsRead: () => void;
}

export function useArticleActivity(
  article: Article | null,
  articleId: string,
  stage: ArticleActivityStage | null,
  isReadingSharedArticle: boolean,
  sharedArticleUtmMedium: string | null,
): UseArticleActivityResult {
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const { recordEvent, subscribeToActivityPings } = useSession();
  const [activeTime, setActiveTime] = useState(0);
  const [wordsRead, setWordsRead] = useState(0);
  const [isArticleRead, setIsArticleRead] = useState(false);
  const quizStageRef = useRef(stage);
  quizStageRef.current = stage;

  useEffect(() => {
    if (!article) return;

    const stopTrackingActivity = subscribeToActivityPings(
      async ({ timestamp, idleTime, activeTime }) => {
        // We are currently not interested in recording idle parts of the session
        await processActivityEvent({ activeTime });
      },
    );

    return () => {
      stopTrackingActivity();
    };
  }, [article]);

  // Function to process and record activity event
  async function processActivityEvent({
    wordsRead,
    activeTime,
  }: {
    wordsRead?: number;
    activeTime?: number;
  }) {
    // No user, no activity tracking
    if (!user) return;

    // No uncommitted activity, no need to record
    if (!wordsRead && !activeTime) return;

    const event = new ArticleActivityEvent(
      articleId,
      activeTime,
      wordsRead,
      quizStageRef.current,
    );

    await recordEvent(event);

    setActiveTime((prev) => prev + (activeTime ?? 0));
    setWordsRead((prev) => prev + (wordsRead ?? 0));
  }

  useEffect(() => {
    // Wait until user is loaded. If unauthenticated, it will be undefined
    if (user === undefined) return;
    if (!article) return;

    if (isReadingSharedArticle) {
      trackEvent(AnalyticsEvent.SHARED_STORY_VIEWED, {
        articleId,
        article: article.title,
        // https://stackoverflow.com/questions/28646433/problems-with-document-referrer
        referrer: document.referrer ?? window.frames.top?.document.referrer,
        medium: sharedArticleUtmMedium,
        createdBy: article.createdByUserId,
        isOwnArticle: user && article.createdByUserId === user.id,
      });
    }
  }, [user, article]);

  useEffect(() => {
    if (!isArticleRead) return;

    trackEvent(AnalyticsEvent.STORY_READING_COMPLETED, {
      articleId,
      article: article?.title,
      timeSpent: activeTime,
    });

    if (isReadingSharedArticle) {
      trackEvent(AnalyticsEvent.SHARED_STORY_COMPLETED, {
        articleId,
        article: article?.title,
        medium: sharedArticleUtmMedium,
        createdBy: article?.createdByUserId,
        isOwnArticle: user && article?.createdByUserId === user.id,
        timeSpent: activeTime,
      });
    }
  }, [isArticleRead]);

  const recordWordsRead = async (wordCount: number) => {
    await processActivityEvent({ wordsRead: wordCount });
  };

  const markArticleAsRead = () => {
    setIsArticleRead(true);
  };

  return {
    // Metrics
    activeTime,
    wordsRead,
    isArticleRead,

    // Actions
    recordWordsRead,
    markArticleAsRead,
  };
}
